import React, { useEffect } from 'react';
import { initSocket } from '@/socket/client';
import { useNotify } from '@/notifications/NotificationProvider';
import { useLocation } from 'wouter';
import axiosInstance from '@/lib/axiosInstance';

export default function SocketBridge({
  token,
  currentUserId,
  getActiveConversationId,
}: {
  token: string;
  currentUserId: string;
  getActiveConversationId: () => string | null; // pass a function from MessagesPage
}) {
  const { notify, requestBrowserPermission } = useNotify();
  const [location, navigate] = useLocation();

  useEffect(() => {
    const sock = initSocket(token);

    const handleNew = async (msg: any) => {
      // Ignore if sent by me
      if (String(msg.sender) === String(currentUserId)) return;

      // Avoid notifying if user is already viewing this conversation
      const active = getActiveConversationId();
      const isActive = active && String(active) === String(msg.conversation);
      if (isActive) return;

      // Fetch minimal convo info to show sender/property title (server could also include it in event)
      try {
        const { data } = await axiosInstance.get(`/apichat/conversations`);
        const conv = (data.data || []).find((c: any) => String(c._id) === String(msg.conversation));
        const title = conv?.counterpart?.name || conv?.property?.title || 'New message';
        const text = msg.text || 'Attachment';

        // In-app toast
        notify({
          title,
          message: text,
          onClick: () => navigate(`/messages?c=${msg.conversation}`),
        });

        // Browser notification (optional)
        if ('Notification' in window) {
          const perm = await requestBrowserPermission();
          if (perm === 'granted') {
            const n = new Notification(title, {
              body: text,
              icon: conv?.counterpart?.avatar || '/icons/chat.png',
              tag: `conv-${msg.conversation}`,
            });
            n.onclick = () => {
              window.focus();
              navigate(`/messages?c=${msg.conversation}`);
              n.close();
            };
          }
        }
      } catch (e) {
        // fallback toast
        notify({
          title: 'New message',
          message: msg.text || 'You have a new message',
          onClick: () => navigate(`/messages?c=${msg.conversation}`),
        });
      }
    };

    sock.on('message:new', handleNew);
    return () => {
      sock.off('message:new', handleNew);
    };
  }, [token, currentUserId, notify, requestBrowserPermission, navigate, getActiveConversationId]);

  return null;
}
